import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import {
  fetchRiskFlowEconReactionQuotes,
  type RiskFlowReactionPoint,
  type RiskFlowReactionQuote,
} from "../../lib/riskflow-econ-reaction-api";
import type { RiskFlowEconPrintAnalysis } from "../../lib/riskflow-econ-print-analysis";
import { buildReactionLineVisualization } from "../../lib/visualization/secondary-visualization-models";
import type { RiskFlowPostSurface } from "./RiskFlowPostCard";

const BklitTimeSeries = lazy(() =>
  import("../visualization/BklitTimeSeries").then((module) => ({
    default: module.BklitTimeSeries,
  })),
);

interface RiskFlowEconReactionFrameProps {
  analysis: RiskFlowEconPrintAnalysis;
  surface: RiskFlowPostSurface;
}

interface ReactionSlide {
  id: string;
  label: string;
  title: string;
  quotes: RiskFlowReactionQuote[];
}

const RATE_LABELS = ["US02Y", "US10Y", "US30Y", "VIX"];
const REACTION_REFRESH_MS = 5 * 60 * 1000;

export function RiskFlowEconReactionFrame({
  analysis,
  surface,
}: RiskFlowEconReactionFrameProps) {
  const [quotes, setQuotes] = useState<RiskFlowReactionQuote[]>([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [status, setStatus] = useState<"loading" | "ready" | "empty">(
    "loading",
  );

  useEffect(() => {
    let active = true;
    setStatus("loading");
    const load = (force = false) => {
      fetchRiskFlowEconReactionQuotes({ force })
        .then((nextQuotes) => {
          if (!active) return;
          setQuotes(nextQuotes);
          setStatus(nextQuotes.length ? "ready" : "empty");
        })
        .catch(() => {
          if (!active) return;
          setQuotes([]);
          setStatus("empty");
        });
    };
    load();
    const timer = window.setInterval(() => load(true), REACTION_REFRESH_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);

  const slides = useMemo(
    () => buildSlides(analysis.instrument, quotes),
    [analysis.instrument, quotes],
  );
  const activeSlide = slides[slideIndex] ?? slides[0] ?? null;

  useEffect(() => {
    setSlideIndex(0);
  }, [analysis.key, analysis.instrument]);

  return (
    <div
      className={`relative flex ${frameHeight(surface)} flex-col overflow-hidden rounded-[6px] border border-[var(--fintheon-accent)]/10 bg-[var(--fintheon-bg)]/50 px-3 py-3`}
      data-riskflow-econ-reaction={analysis.key}
      aria-label={`${analysis.title} market reaction charts`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--fintheon-text)]/78">
            Reaction Tape
          </p>
        </div>
        <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--fintheon-text)]/35">
          {statusLabel(status)}
        </span>
      </div>

      <div className="mt-3 min-h-0 flex-1">
        {activeSlide ? (
          <ReactionSlideChart slide={activeSlide} />
        ) : (
          <div className="flex h-full items-center justify-center font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--fintheon-text)]/35">
            Awaiting macro feed
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-[var(--fintheon-accent)]/8 pt-2">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setSlideIndex(index);
            }}
            className={`font-mono text-[10px] uppercase tracking-[0.12em] transition-[color,text-shadow] ${
              index === slideIndex
                ? "text-[var(--fintheon-accent)] [text-shadow:0_0_10px_rgba(199,159,74,0.42)]"
                : "text-[var(--fintheon-text)]/38 hover:text-[var(--fintheon-accent)] hover:[text-shadow:0_0_10px_rgba(199,159,74,0.36)]"
            }`}
            aria-label={`Show ${slide.title} reaction chart`}
          >
            {slide.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ReactionSlideChart({ slide }: { slide: ReactionSlide }) {
  const series = slide.quotes
    .map((quote) => ({
      quote,
      points: selectPoints(quote),
      tone: sparklineTone(quote.change),
    }))
    .filter((item) => item.points.length >= 2);
  const model = buildReactionLineVisualization({
    id: `riskflow-reaction-${slide.id}`,
    title: slide.title,
    series: series.map((item) => ({
      label: item.quote.label,
      color: sparklineColor(item.tone),
      points: item.points,
    })),
  });

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[13px] font-semibold uppercase tracking-[0.12em] text-[var(--fintheon-text)]/78">
            {slide.title}
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-text)]/32">
            {series.length ? "5m bars" : "history pending"}
          </p>
        </div>
        <div className="shrink-0 text-right font-mono tabular-nums">
          {slide.quotes.map((quote) => (
            <p key={quote.label} className={changeClass(quote.change)}>
              {formatPrice(quote.price)} {formatPercent(quote.changePercent)}
            </p>
          ))}
        </div>
      </div>
      <div className="mt-3 min-h-0 flex-1">
        {series.length ? (
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-[10px] text-[var(--fintheon-text)]/35">
                Loading reaction history
              </div>
            }
          >
            <BklitTimeSeries
              height="100%"
              model={model}
              showDatePill
              showGrid
              showXAxis={false}
              showYAxis={false}
              strokeWidth={3}
              tooltipValueFormatter={(_, seriesKey, point) => {
                const raw = point[`${seriesKey}Raw`];
                return typeof raw === "number" ? formatPrice(raw) : null;
              }}
              xLabelFormatter={(date) =>
                date.toLocaleString([], {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })
              }
            />
          </Suspense>
        ) : (
          <div className="flex h-full items-center justify-center border-t border-[var(--fintheon-accent)]/8 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--fintheon-text)]/35">
            Awaiting 5d feed
          </div>
        )}
      </div>
    </div>
  );
}

function sparklineTone(
  change: number | null,
): "primary" | "bullish" | "bearish" {
  if (change == null) return "primary";
  if (change > 0) return "bullish";
  if (change < 0) return "bearish";
  return "primary";
}

function sparklineColor(tone: ReturnType<typeof sparklineTone>) {
  if (tone === "bullish") return "var(--fintheon-bullish)";
  if (tone === "bearish") return "var(--fintheon-bearish)";
  return "var(--fintheon-primary, var(--fintheon-accent))";
}

function buildSlides(
  instrument: string,
  quotes: RiskFlowReactionQuote[],
): ReactionSlide[] {
  const byLabel = new Map(quotes.map((quote) => [quote.label, quote]));
  const selected = instrumentLabel(instrument);
  const selectedQuote = byLabel.get(selected) ?? byLabel.get("NQ") ?? null;
  const quoteFor = (label: string) => byLabel.get(label) ?? null;
  return [
    slide(
      "instrument",
      `/${selectedQuote?.label ?? selected}`,
      "Selected Instrument",
      [selectedQuote],
    ),
    slide("us02y", "02Y", "US 02Y Yield", [quoteFor("US02Y")]),
    slide("us10y", "10Y", "US 10Y Yield", [quoteFor("US10Y")]),
    slide("us30y-vix", "30Y/VIX", "US 30Y + VIX", [
      quoteFor("US30Y"),
      quoteFor("VIX"),
    ]),
  ];
}

function slide(
  id: string,
  label: string,
  title: string,
  quotes: Array<RiskFlowReactionQuote | null>,
): ReactionSlide {
  return { id, label, title, quotes: quotes.filter(isReactionQuote) };
}

function isReactionQuote(
  value: RiskFlowReactionQuote | null,
): value is RiskFlowReactionQuote {
  return Boolean(value);
}

function selectPoints(quote: RiskFlowReactionQuote): RiskFlowReactionPoint[] {
  if (quote.sparkline5d.length >= 2) return quote.sparkline5d;
  if (quote.sparkline1d.length >= 2) return quote.sparkline1d;
  return quote.sparkline;
}

function instrumentLabel(instrument: string): string {
  const normalized = instrument.replace(/^[/$]/, "").toUpperCase();
  return RATE_LABELS.includes(normalized) ? normalized : normalized || "NQ";
}

function formatPrice(value: number | null): string {
  if (value == null) return "--";
  if (Math.abs(value) >= 1000)
    return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
  if (Math.abs(value) >= 10) return value.toFixed(2);
  return value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

function formatPercent(value: number | null): string {
  if (value == null) return "";
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
}

function changeClass(value: number | null): string {
  if (value == null) return "text-[var(--fintheon-text)]/35";
  return value >= 0 ? "text-[var(--fintheon-bullish)]" : "text-[#ff4d57]";
}

function statusLabel(status: "loading" | "ready" | "empty") {
  if (status === "loading") return "[LOADING]";
  if (status === "empty") return "[PARTIAL]";
  return "[LIVE]";
}

function frameHeight(surface: RiskFlowPostSurface): string {
  return surface === "mini" ? "h-40" : "h-72";
}
